import React, { useState, useEffect, useCallback } from "react";
import { Dialog } from "@headlessui/react";
import { useMaps } from "../components/MapsProvider";
import PickupMapModal from "../components/PickupMapModal";
import DropoffMapModal from "../components/DropoffMapModal";
import LocationSearchMap from "../components/LocationSearchMap";
import CustomDateTimePicker from "../components/CustomDateTimePicker";
import { FiAlertCircle, FiMapPin, FiTruck, FiAlertTriangle, FiX } from "react-icons/fi";
import fleetService from "../services/FleetService";
import VanSchedulerService from "../services/VanSchedulerService";
import { toast } from "react-hot-toast";
import { supabase } from "../services/SupabaseService";
import { GoogleMap, Marker, DirectionsRenderer } from "@react-google-maps/api";

// Default location for Lasmari Apraos 
const DEFAULT_LOCATION = {
  name: "Lasmari Apraos",
  lat: 39.796276, 
  lng: 19.884997
};

// Lasmari coordinates for map centering
const lasmariCoords = { lat: 39.796276, lng: 19.884997 };

// Dark mode map styles
const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#0f0f0f" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0f0f0f" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#181818" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#2a2a2a" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1a1a1a" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#2c2c2c" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f1f1f" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#1f1f1f" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#000000" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3d3d3d" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#0f0f0f" }],
  },
];

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  passengers: 1,
  luggage: 0,
  pickup_name: "",
  pickup_lat: null,
  pickup_lng: null,
  dropoff_name: "",
  dropoff_lat: null,
  dropoff_lng: null,
  datetime: "",
  price: 0,
  distance: "",
  duration: "",
  status: "pending",
  comments: "",
  price_manually_set: false
};

// Status options for the booking
const statusOptions = ["pending", "confirmed", "completed", "cancelled"];

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full w-full">
    <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
    <p className="ml-3 text-white">Loading...</p>
  </div>
);

const BookingModal = ({ booking, isOpen, onClose, onSave }) => {
  // Declare ALL hooks at the top to avoid reference errors
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [routeDetails, setRouteDetails] = useState(null);
  const [basePrice, setBasePrice] = useState(0); // Store the calculated base price
  const [vansNeeded, setVansNeeded] = useState(1); // Track number of vans needed
  
  // Location selection states (same as guest booking)
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isDropoffModalOpen, setIsDropoffModalOpen] = useState(false);
  
  // Date picker state
  const [datetime, setDatetime] = useState(null);
  
  // Map and directions (same as guest booking)
  const [directions, setDirections] = useState(null);
  const [isLoadingDirections, setIsLoadingDirections] = useState(false);
  const { isLoaded, createMarkerIcon, mapOptions } = useMaps();
  
  const [availableVans, setAvailableVans] = useState([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityWarning, setAvailabilityWarning] = useState(null);
  const [isDateTimeAvailable, setIsDateTimeAvailable] = useState(true);

  useEffect(() => {
    // If editing, populate the form
    if (booking) {
      // Properly handle the date to ensure consistent timezone
      const bookingDate = new Date(booking.datetime);
      
      console.log('Original booking datetime:', booking.datetime);
      console.log('Parsed booking date:', bookingDate);
      
      setFormData({
        ...booking,
        price_manually_set: booking.price_manually_set || false
      });
      
      // Set datetime for date picker
      setDatetime(bookingDate);
      
      // Set pickup and dropoff locations
      if (booking.pickup_lat && booking.pickup_lng) {
        setPickup({
          name: booking.pickup_name,
          position: { lat: booking.pickup_lat, lng: booking.pickup_lng },
          lat: booking.pickup_lat,
          lng: booking.pickup_lng
        });
      }
      
      if (booking.dropoff_lat && booking.dropoff_lng) {
        setDropoff({
          name: booking.dropoff_name,
          position: { lat: booking.dropoff_lat, lng: booking.dropoff_lng },
          lat: booking.dropoff_lat,
          lng: booking.dropoff_lng
        });
      }
      
      // If the booking has route details, set them
      if (booking.distance && booking.duration) {
        setRouteDetails({
          distance: booking.distance,
          duration: booking.duration
        });
        
        // Store the base price if available
        if (booking.base_price) {
          setBasePrice(booking.base_price);
        } else {
          // Estimate base price from the current price
          const estimatedBasePrice = calculateBasePrice(booking.price, booking.passengers);
          setBasePrice(estimatedBasePrice);
        }

        // Calculate vans needed
        const vans = Math.ceil(booking.passengers / 7);
        setVansNeeded(vans);
      }
    } else {
      // Reset form for new booking
      setFormData(initialState);
      setPickup(null);
      setDropoff(null);
      setDatetime(null);
      setRouteDetails(null);
      setBasePrice(0);
      setVansNeeded(1);
      setDirections(null);
    }
  }, [booking, isOpen]);

  // Calculate base price helper
  const calculateBasePrice = (totalPrice, passengers) => {
    // Estimate base price per van (before passenger multipliers)
    const vansCount = Math.ceil(passengers / 7);
    return totalPrice / vansCount;
  };

  // Calculate final price based on passengers
  const calculateFinalPrice = (basePricePerVan, passengerCount) => {
    const vansCount = Math.ceil(passengerCount / 7);
    
    let totalPrice = 0;
    let remainingPassengers = passengerCount;
    
    for (let van = 0; van < vansCount; van++) {
      const passengersInThisVan = Math.min(7, remainingPassengers);
      remainingPassengers -= passengersInThisVan;
      
      let vanPrice = basePricePerVan;
      if (passengersInThisVan > 1) {
        const additionalPassengers = passengersInThisVan - 1;
        vanPrice += (vanPrice * 0.2 * additionalPassengers);
      }
      
      totalPrice += vanPrice;
    }
    
    return totalPrice;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "price") {
      // When price is manually changed, set the flag to true
      setFormData(prev => ({
        ...prev,
        [name]: Number(value) || 0,
        price_manually_set: true
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === "passengers" || name === "luggage" 
          ? Number(value) || (name === "passengers" ? 1 : 0)
          : value
      }));
      
      // Update vans needed when passengers change
      if (name === "passengers") {
        const passengerCount = Number(value) || 1;
        const newVansNeeded = Math.ceil(passengerCount / 7);
        setVansNeeded(newVansNeeded);
        
        // Auto-adjust luggage if it exceeds the new limit
        const maxLuggage = newVansNeeded * 8;
        const currentLuggage = formData.luggage || 0;
        
        if (currentLuggage > maxLuggage) {
          setFormData(prev => ({
            ...prev,
            luggage: maxLuggage
          }));
        }
        
        // Recalculate price if passengers changes (unless price was manually set)
        if (!formData.price_manually_set && routeDetails) {
          const finalPrice = calculateFinalPrice(basePrice, passengerCount);
          
          // Update price directly
          setFormData(prev => ({
            ...prev,
            price: parseFloat(finalPrice.toFixed(2))
          }));
        }
      }
      
      // Validate luggage limit when luggage changes
      if (name === "luggage") {
        const luggageCount = Number(value) || 0;
        const passengerCount = formData.passengers || 1;
        const vansNeeded = Math.ceil(passengerCount / 7);
        const maxLuggage = vansNeeded * 8;
        
        if (luggageCount > maxLuggage) {
          // Don't exceed the limit
          setFormData(prev => ({
            ...prev,
            luggage: maxLuggage
          }));
          return; // Exit early to prevent setting the original value
        }
      }
    }
  };

  // Handle focus on number inputs - select all for easy editing
  const handleNumberFocus = (e) => {
    // Always select all text so user can easily replace the value
    e.target.select();
  };

  // Handle blur (when user leaves the field) to validate minimum values
  const handleNumberBlur = (e) => {
    const { name, value } = e.target;
    
    if (name === "passengers") {
      const numValue = Number(value);
      if (!numValue || numValue < 1) {
        // If passengers is empty or 0, set it back to 1
        setFormData(prev => ({
          ...prev,
          passengers: 1
        }));
      }
    } else if (name === "luggage") {
      const numValue = Number(value);
      if (isNaN(numValue) || numValue < 0) {
        // If luggage is invalid, set it back to 0
        setFormData(prev => ({
          ...prev,
          luggage: 0
        }));
      }
    }
  };

  // Handle price focus specifically
  const handlePriceFocus = (e) => {
    if (!formData.price_manually_set) {
      // Enable manual pricing when user clicks on price field
      setFormData(prev => ({
        ...prev,
        price_manually_set: true
      }));
    }
    e.target.select(); // Select all text for easier editing
  };

  // Handle date change from CustomDateTimePicker  
  const handleDateTimeChange = (formattedDateString) => {
    setFormData(prev => ({
      ...prev,
      datetime: formattedDateString
    }));
  };

  // Handle date change callback (receives Date object)
  const handleDateChange = (date) => {
    setDatetime(date);
  };

  // Update vansNeeded whenever passengers change (additional safety net)
  useEffect(() => {
    const newVansNeeded = Math.ceil(formData.passengers / 7);
    if (newVansNeeded !== vansNeeded) {
      setVansNeeded(newVansNeeded);
    }
  }, [formData.passengers, vansNeeded]);

  // Handle the reset of price calculation (when user wants to go back to automatic)
  const handleResetPriceCalculation = () => {
    if (routeDetails) {
      // Reset price_manually_set flag
      setFormData(prev => ({
        ...prev,
        price_manually_set: false
      }));
      
      // Recalculate the price based on the current route details
      if (routeDetails.distance) {
        // Extract numeric value from distance string
        const distanceValue = parseFloat(routeDetails.distance.replace(/[^0-9.]/g, ''));
        const distanceUnit = routeDetails.distance.includes('km') ? 'km' : 'mi';
        
        // Convert to km if in miles
        const distanceInKm = distanceUnit === 'km' ? distanceValue : distanceValue * 1.60934;
        
        // Base price €3 + €2 per km + €1 per luggage
        const basePriceValue = (3 + (distanceInKm * 2) + formData.luggage).toFixed(2);
        
        // Store the calculated base price
        setBasePrice(Number(basePriceValue));
        
        // Calculate final price based on passengers
        const finalPrice = calculateFinalPrice(Number(basePriceValue), formData.passengers);
        
        setFormData(prev => ({
          ...prev,
          price: parseFloat(finalPrice.toFixed(2)),
          price_manually_set: false
        }));
      }
    }
  };

  // Calculate route and price when both locations are selected (same as guest booking)
  useEffect(() => {
    if (pickup && dropoff && window.google && window.google.maps) {
      const calculateRoute = async () => {
        setIsLoadingDirections(true);
        try {
          const directionsService = new window.google.maps.DirectionsService();
          
          const result = await new Promise((resolve, reject) => {
            directionsService.route(
              {
                origin: pickup.position,
                destination: dropoff.position,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  resolve(result);
                } else {
                  reject(new Error(`Directions request failed: ${status}`));
                }
              }
            );
          });

          setDirections(result);
          const route = result.routes[0];
          const leg = route.legs[0];
          
          const distance = leg.distance.text;
          const duration = leg.duration.text;
          
          setRouteDetails({ distance, duration });
          
          // Update form data
          setFormData(prev => ({
            ...prev,
            distance,
            duration
          }));
          
          // Calculate price if not manually set
          if (!formData.price_manually_set) {
            const distanceValue = parseFloat(distance.replace(/[^0-9.]/g, ''));
            const distanceInKm = distance.includes('km') ? distanceValue : distanceValue * 1.60934;
            
            // Base price calculation
            const basePriceValue = 3 + (distanceInKm * 2) + formData.luggage;
            setBasePrice(basePriceValue);
            
            // Calculate final price
            const finalPrice = calculateFinalPrice(basePriceValue, formData.passengers);
            
            setFormData(prev => ({
              ...prev,
              price: parseFloat(finalPrice.toFixed(2))
            }));
          }
          
        } catch (error) {
          console.error('Error calculating route:', error);
          setError('Failed to calculate route. Please try again.');
        } finally {
          setIsLoadingDirections(false);
        }
      };

      calculateRoute();
    }
  }, [pickup, dropoff, formData.luggage, formData.passengers, formData.price_manually_set]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Validation
      if (!formData.full_name || !formData.email || !formData.phone) {
        throw new Error("Please fill in all required customer information.");
      }

      if (!pickup || !dropoff) {
        throw new Error("Please select both pickup and dropoff locations.");
      }

      if (!datetime) {
        throw new Error("Please select date and time.");
      }

      if (formData.passengers < 1) {
        throw new Error("Please specify the number of passengers.");
      }

      // Calculate vans needed
      const requiredVans = Math.ceil(formData.passengers / 7);

      // Convert date to ISO string
      const datetimeISO = datetime.toISOString();

      // Prepare booking data for parent component to save
      const bookingData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        passengers: formData.passengers,
        luggage: formData.luggage,
        pickup_name: pickup.name,
        pickup_lat: pickup.position.lat,
        pickup_lng: pickup.position.lng,
        dropoff_name: dropoff.name,
        dropoff_lat: dropoff.position.lat,
        dropoff_lng: dropoff.position.lng,
        datetime: datetimeISO,
        distance: formData.distance,
        duration: formData.duration,
        price: formData.price,
        base_price: basePrice,
        status: formData.status || "pending",
        price_manually_set: formData.price_manually_set || false,
        vans_needed: requiredVans
      };

      // Include booking ID if we're updating an existing booking
      if (booking && booking.id) {
        bookingData.id = booking.id;
      }

      console.log('📝 Submitting booking data:', bookingData);

      // Call the parent's save function (handles backend/database logic):
      if (onSave) {
        await onSave(bookingData);
      } else {
        // Fallback to direct Supabase insert if no onSave provided
        const { data: insertedBooking, error: insertError } = await supabase
          .from("bookings")
          .insert([bookingData])
          .select();

        if (insertError) {
          console.error("❌ Booking insertion failed:", insertError);
          setError("Failed to create booking. Please try again.");
          setIsSubmitting(false);
          return;
        }

        console.log('✅ Booking created successfully via fallback:', insertedBooking);
        toast.success("Booking created successfully!");
      }
      
      // Close modal on success
      onClose();
    } catch (err) {
      console.error("❌ Unexpected error in handleSubmit:", err);
      
      // Handle specific error cases
      if (err.message) {
        setError(err.message);
        toast.error(err.message);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
        toast.error(err.response.data.error);
      } else {
        setError("An unexpected error occurred. Please try again.");
        toast.error("Failed to save booking. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Get passenger pricing tier info
  const getPassengerPricingInfo = () => {
    const vans = Math.ceil(formData.passengers / 7);
    
    if (vans === 1) {
      return "1 van required";
    }
    
    return `${vans} vans required`;
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog 
      open={isOpen} 
      onClose={() => !isSubmitting && onClose()}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex items-center justify-center min-h-screen">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-80" />
        
        <div className="bg-[#0a0a0a] relative rounded-xl shadow-2xl max-w-6xl w-full mx-4 my-8 max-h-[90vh] overflow-hidden border border-white/20">
          <div className="px-6 py-4 border-b border-white/20 flex justify-between items-center bg-gradient-to-r from-[#111] to-[#111] shadow-md">
            <Dialog.Title className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-white">
              {booking ? "Edit Booking" : "New Booking"}
            </Dialog.Title>
            
            <button
              disabled={isSubmitting}
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>
          
          <div className="overflow-y-auto p-6 max-h-[calc(90vh-4rem)]">
            {isSubmitting ? (
              <LoadingSpinner />
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Location Selection Section (same as guest booking) */}
                <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                  <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                    Locations
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="flex items-center text-sm font-medium mb-2 text-white">
                        <FiMapPin className="mr-2 text-white" />
                        Pickup Location
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsPickupModalOpen(true)}
                        className="w-full p-4 bg-black border-0 rounded-xl text-left hover:bg-[#0a0a0a] transition-colors duration-300 flex items-center outline-none focus:outline-none focus:ring-1 focus:ring-white/30 ring-offset-0"
                      >
                        <span className="flex-1 text-white">{pickup?.name || "Select pickup location"}</span>
                        <FiMapPin className="text-white" />
                      </button>
                    </div>

                    <div>
                      <label className="flex items-center text-sm font-medium mb-2 text-white">
                        <FiMapPin className="mr-2 text-white" />
                        Drop-off Location
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsDropoffModalOpen(true)}
                        className="w-full p-4 bg-black border-0 rounded-xl text-left hover:bg-[#0a0a0a] transition-colors duration-300 flex items-center outline-none focus:outline-none focus:ring-1 focus:ring-white/30 ring-offset-0"
                      >
                        <span className="flex-1 text-white">{dropoff?.name || "Select drop-off location"}</span>
                        <FiMapPin className="text-white" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Route Map (same as guest booking) */}
                  {pickup && dropoff && isLoaded && (
                    <div className="relative mt-6">
                      <div className="h-[400px] overflow-hidden rounded-xl relative">
                        {isLoadingDirections && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
                            <div className="animate-spin h-12 w-12 border-4 border-white rounded-full border-t-transparent"></div>
                          </div>
                        )}
                        
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={pickup?.position || lasmariCoords}
                          zoom={8.5}
                          options={{
                            ...mapOptions,
                            minZoom: 6,
                            maxZoom: 18
                          }}
                        >
                          {directions && (
                            <DirectionsRenderer
                              key={`directions-${Date.now()}`}
                              directions={directions}
                              options={{
                                suppressMarkers: true,
                                polylineOptions: {
                                  strokeColor: "#ffffff",
                                  strokeWeight: 5,
                                  strokeOpacity: 0.7
                                }
                              }}
                            />
                          )}
                          
                          {pickup && (
                            <Marker 
                              position={pickup.position} 
                              icon={createMarkerIcon('pickup')}
                              animation={window.google.maps.Animation.DROP}
                            />
                          )}
                          
                          {dropoff && (
                            <Marker 
                              position={dropoff.position} 
                              icon={createMarkerIcon('dropoff')}
                              animation={window.google.maps.Animation.DROP}
                            />
                          )}
                        </GoogleMap>
                      </div>
                      
                      {/* Route information */}
                      {routeDetails && (
                        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                          <div className="text-white">
                            <div className="text-sm text-white">Distance</div>
                            <div className="font-semibold">{routeDetails.distance}</div>
                          </div>
                          <div className="text-white">
                            <div className="text-sm text-white">Duration</div>
                            <div className="font-semibold">{routeDetails.duration}</div>
                          </div>
                          <div className="text-white">
                            <div className="text-sm text-white">Passenger Level</div>
                            <div className="font-semibold">{getPassengerPricingInfo()}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Customer Information Section */}
                  <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20 space-y-5">
                    <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20">
                      Customer Information
                    </h3>
                    <div className="grid grid-cols-1 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Full Name*
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          required
                          className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                          placeholder="Customer name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Email*
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                          placeholder="email@example.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Phone*
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                          placeholder="+30 694 555 1234"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">
                            Passengers*
                          </label>
                          <input
                            type="number"
                            name="passengers"
                            min="1"
                            value={formData.passengers}
                            onChange={handleChange}
                            onFocus={handleNumberFocus}
                            onBlur={handleNumberBlur}
                            required
                            className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                            placeholder="Number of passengers"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-white mb-1">
                            Luggage
                            {(() => {
                              const passengerCount = formData.passengers || 1;
                              const vansNeeded = Math.ceil(passengerCount / 7);
                              const maxLuggage = vansNeeded * 8;
                              return (
                                <span className="ml-2 text-xs text-gray-400">
                                  (max {maxLuggage} for {vansNeeded} van{vansNeeded > 1 ? 's' : ''})
                                </span>
                              );
                            })()}
                          </label>
                          <input
                            type="number"
                            name="luggage"
                            min="0"
                            max={Math.ceil((formData.passengers || 1) / 7) * 8}
                            value={formData.luggage}
                            onChange={handleChange}
                            onFocus={handleNumberFocus}
                            onBlur={handleNumberBlur}
                            className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                            placeholder="Number of luggage items"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Status
                        </label>
                        <select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                        >
                          {statusOptions.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Special Requests / Comments
                        </label>
                        <input
                          type="text"
                          name="comments"
                          value={formData.comments}
                          onChange={handleChange}
                          maxLength={500}
                          className="block w-full bg-[#111] border border-white/30 rounded-lg shadow-inner py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                          placeholder="Any special requests? (e.g., child seat, wheelchair access, pickup instructions)"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Optional: Child seats, accessibility needs, luggage assistance, etc. (max 500 characters)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Booking Details Section */}
                  <div className="space-y-6">
                    {/* Date and Time */}
                    <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                      <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4">
                        Date & Time
                      </h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-white mb-1">
                          Pickup Date & Time*
                        </label>
                        <CustomDateTimePicker
                          selectedDate={datetime}
                          onDateChange={(date) => {
                            setDatetime(date);
                            // Immediately update form data when date changes
                            if (date) {
                              const year = date.getFullYear();
                              const month = (date.getMonth() + 1).toString().padStart(2, '0');
                              const day = date.getDate().toString().padStart(2, '0');
                              const hours = date.getHours().toString().padStart(2, '0');
                              const minutes = date.getMinutes().toString().padStart(2, '0');
                              const formattedValue = `${year}-${month}-${day}T${hours}:${minutes}`;
                              
                              setFormData(prev => ({
                                ...prev,
                                datetime: formattedValue
                              }));
                            }
                          }}
                          placeholder="Select date and time"
                        />
                      </div>
                    </div>

                    {/* Price Calculation */}
                    <div className="bg-black/40 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-bold text-white pb-2 border-b border-white/20 mb-4 w-full">
                          Price Calculation
                        </h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-xl text-white">
                              €
                            </span>
                            <input
                              type="number"
                              name="price"
                              min="0"
                              step="0.01"
                              value={formData.price || ''}
                              onChange={handleChange}
                              onFocus={handlePriceFocus}
                              disabled={!formData.price_manually_set}
                              className={`block w-full pl-10 pr-4 py-3 text-2xl font-bold ${
                                formData.price_manually_set ? 'bg-[#111] text-white' : 'bg-[#0a0a0a] text-white cursor-not-allowed'
                              } border border-white/30 rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent`}
                              placeholder="0.00"
                            />
                          </div>
                          
                          {/* Base price info */}
                          {basePrice > 0 && (
                            <div className="mt-2 text-sm text-white">
                              Base price: €{basePrice.toFixed(2)}
                              {vansNeeded > 1 && ` × ${vansNeeded} vans`}
                            </div>
                          )}
                          
                          <div className="mt-4 flex justify-between">
                            <label className="flex items-center text-sm text-white">
                              <input
                                type="checkbox"
                                name="price_manually_set"
                                checked={formData.price_manually_set}
                                onChange={(e) => {
                                  const isManual = e.target.checked;
                                  setFormData(prev => ({
                                    ...prev,
                                    price_manually_set: isManual
                                  }));
                                  
                                  // If switching to manual, focus the price input
                                  if (isManual) {
                                    setTimeout(() => {
                                      const priceInput = document.querySelector('input[name="price"]');
                                      if (priceInput) {
                                        priceInput.focus();
                                        priceInput.select();
                                      }
                                    }, 100);
                                  }
                                }}
                                className="h-4 w-4 text-white border-white rounded focus:ring-white"
                              />
                              <span className="ml-2">Set price manually</span>
                            </label>
                            
                            <button
                              type="button"
                              onClick={handleResetPriceCalculation}
                              className="px-3 py-1 bg-black hover:bg-white/20 text-white text-sm rounded-md border border-white/30 transition-colors"
                            >
                              Recalculate
                            </button>
                          </div>
                        </div>
                        
                        <div className="text-sm text-white space-y-2">
                          <div>Passengers: {formData.passengers}</div>
                          <div>Vans needed: {vansNeeded}</div>
                          {routeDetails && (
                            <>
                              <div>Distance: {routeDetails.distance}</div>
                              <div>Duration: {routeDetails.duration}</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div className="p-4 bg-red-900/30 border border-red-500/30 rounded-lg text-red-400">
                    {error}
                  </div>
                )}
                
                {/* Action buttons */}
                <div className="flex justify-end space-x-4 pt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 bg-white hover:bg-gray-200 text-black rounded-lg transition-colors disabled:opacity-50 flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      booking ? "Update Booking" : "Create Booking"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      
      {/* Location Selection Modals (same as guest booking) */}
      <PickupMapModal
        isOpen={isPickupModalOpen}
        onClose={() => setIsPickupModalOpen(false)}
        onConfirm={(location) => {
          setPickup({
            name: location.name,
            position: location.position,
            lat: location.position.lat,
            lng: location.position.lng
          });
          setIsPickupModalOpen(false);
        }}
        initialLocation={pickup}
      />

      <DropoffMapModal
        isOpen={isDropoffModalOpen}
        onClose={() => setIsDropoffModalOpen(false)}
        onConfirm={(location) => {
          setDropoff({
            name: location.name,
            position: location.position,
            lat: location.position.lat,
            lng: location.position.lng
          });
          setIsDropoffModalOpen(false);
        }}
        initialLocation={dropoff}
      />
    </Dialog>
  );
};

export default BookingModal;